import React from 'react'
import { Music2, RefreshCw } from 'lucide-react'
import { cn } from '../lib/utils'
import {
  getProcessingArtist,
  getProcessingProgress,
  getProcessingStage,
  getProcessingThumbnailUrl,
  getProcessingTitle,
} from '../utils/processingJob'
import { Card } from './ui'

const ProcessingJobRow = ({ job, isDarkMode, isPracticePage, onRetry }) => {
  const [thumbnailFailed, setThumbnailFailed] = React.useState(false)
  const title = getProcessingTitle(job)
  const artist = getProcessingArtist(job)
  const thumbnailUrl = getProcessingThumbnailUrl(job)
  const progress = getProcessingProgress(job)
  const stage = getProcessingStage(job)
  const isFailed = job.status === 'failed'
  const failureLabel = job.jobType === 'lyrics_cleanup'
    ? 'Lyrics refresh failed'
    : 'Song processing failed'

  React.useEffect(() => {
    setThumbnailFailed(false)
  }, [thumbnailUrl])

  return (
    <div className={cn(
      'flex items-center gap-3 rounded-xl border p-2.5',
      isPracticePage
        ? 'border-white/15 bg-black/10'
        : isDarkMode
          ? 'border-gray-600 bg-gray-700/30'
          : 'border-blue-200 bg-blue-50'
    )}>
      <div className="flex h-12 w-16 shrink-0 items-center justify-center overflow-hidden rounded-lg bg-black/20">
        {thumbnailUrl && !thumbnailFailed ? (
          <img
            src={thumbnailUrl}
            alt=""
            className="h-full w-full object-cover"
            onError={() => setThumbnailFailed(true)}
          />
        ) : (
          <Music2 className="h-4 w-4 opacity-60" aria-hidden />
        )}
      </div>

      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-semibold">{title}</p>
        <p className="truncate text-[11px] opacity-65">{artist}</p>
        <div className="mt-1.5 flex items-center justify-between gap-2 text-[10px] opacity-70">
          <span className="truncate">{isFailed ? failureLabel : stage}</span>
          <span className="shrink-0 tabular-nums">{progress}%</span>
        </div>
        {isFailed && onRetry ? (
          <button
            type="button"
            className="mt-2 rounded-md border border-current/30 px-2 py-1 text-[11px] font-semibold"
            onClick={() => onRetry(job.jobId)}
          >
            Retry
          </button>
        ) : null}
        <div
          className="mt-1 h-1 overflow-hidden rounded-full bg-black/20"
          role="progressbar"
          aria-label={`${title} processing progress`}
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={progress}
        >
          <div
            className={cn('h-full rounded-full transition-[width] duration-500', isPracticePage ? 'bg-white' : 'bg-blue-500')}
            style={{ width: `${progress}%` }}
          />
        </div>
      </div>
    </div>
  )
}

const ProcessingJobsStatus = ({ userActiveJobs, isDarkMode, isPracticePage = false, onRetry }) => {
  if (userActiveJobs.length === 0) {
    return null
  }

  const visibleJobs = userActiveJobs.slice(0, 3)
  const remainingCount = userActiveJobs.length - visibleJobs.length

  return (
    <Card
      className={cn(
        'p-4 transition-colors duration-300',
        isPracticePage
          ? 'border-white/20 bg-white/10 text-white backdrop-blur-sm'
          : isDarkMode
            ? 'border-white/10 bg-black/70 text-gray-200'
            : 'border-white/40 bg-white/85 text-blue-900'
      )}
    >
      <div className="mb-3 flex items-center gap-2">
        <RefreshCw className="h-4 w-4 animate-spin opacity-70" aria-hidden />
        <span className="text-sm font-medium">
          Processing your songs ({userActiveJobs.length})
        </span>
      </div>

      <div className="space-y-2">
        {visibleJobs.map((job) => (
          <ProcessingJobRow
            key={job.jobId}
            job={job}
            isDarkMode={isDarkMode}
            isPracticePage={isPracticePage}
            onRetry={onRetry}
          />
        ))}
        {remainingCount > 0 ? (
          <p className="text-xs opacity-60">And {remainingCount} more...</p>
        ) : null}
      </div>
    </Card>
  )
}

export default ProcessingJobsStatus
